/**
 * 可拖拽 / 可最小化的悬浮窗 — 不阻塞页面其它操作.
 *
 * - position: fixed, 用户可拖动标题栏改变位置
 * - 无 mask, 底层 UI 可继续点击 / 滚动 / 输入
 * - 标题栏右上角: 最小化 (折叠 body 仅留标题栏) / 关闭
 * - 位置 + 最小化状态用 localStorage 持久化, 下次打开恢复
 */
import { CloseOutlined, MinusOutlined, PushpinOutlined } from '@ant-design/icons';
import { Button, Space, Typography } from 'antd';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useIsMobile } from '../../utils/use-is-mobile.js';

interface Pos {
  x: number;
  y: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  extra?: ReactNode;
  width?: number;
  height?: number;
  children: ReactNode;
  /** localStorage key prefix, 默认 'sip_panel' */
  storageKey?: string;
  /** 主体内边距, 默认 12; 自定义布局 (如手机壳) 传 0 由内部自管 */
  bodyPadding?: number;
  /** 主体是否自动 overflow, 默认 true; 自定义布局可关 */
  bodyScroll?: boolean;
}

const DEFAULT_W = 420;
const DEFAULT_H = 560;

function readPos(key: string): Pos | null {
  try {
    const raw = localStorage.getItem(`${key}_pos`);
    if (!raw) return null;
    const p = JSON.parse(raw) as Pos;
    if (typeof p.x === 'number' && typeof p.y === 'number') return p;
  } catch {}
  return null;
}

function readMinimized(key: string): boolean {
  return localStorage.getItem(`${key}_min`) === '1';
}

function clampPos(p: Pos, w: number, _h: number): Pos {
  if (typeof window === 'undefined') return p;
  const maxX = window.innerWidth - 80; // 至少留出关闭按钮可点
  const maxY = window.innerHeight - 40;
  return {
    x: Math.min(Math.max(0, p.x), Math.max(0, maxX - w + 80)),
    y: Math.min(Math.max(0, p.y), Math.max(0, maxY)),
  };
}

export function FloatingPanel({
  open,
  onClose,
  title,
  extra,
  width = DEFAULT_W,
  height = DEFAULT_H,
  children,
  storageKey = 'sip_panel',
  bodyPadding = 12,
  bodyScroll = true,
}: Props) {
  // 移动端：悬浮窗改为全屏（不可拖拽），避免 420px 宽面板超出手机屏
  const isMobile = useIsMobile();
  const [pos, setPos] = useState<Pos>(() => {
    const saved = readPos(storageKey);
    if (saved) return saved;
    // 默认右下角, 留出一定边距
    if (typeof window !== 'undefined') {
      return {
        x: Math.max(0, window.innerWidth - width - 24),
        y: Math.max(0, window.innerHeight - height - 24),
      };
    }
    return { x: 100, y: 100 };
  });
  const [minimized, setMinimized] = useState<boolean>(() => readMinimized(storageKey));
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(
    null,
  );

  // 持久化
  useEffect(() => {
    localStorage.setItem(`${storageKey}_pos`, JSON.stringify(pos));
  }, [pos, storageKey]);
  useEffect(() => {
    localStorage.setItem(`${storageKey}_min`, minimized ? '1' : '0');
  }, [minimized, storageKey]);

  // 窗口 resize 时确保面板不跑到屏外
  useEffect(() => {
    if (!open) return;
    const onResize = () => setPos((p) => clampPos(p, width, height));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, width, height]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // 仅在标题栏拖动 (非按钮)
      if ((e.target as HTMLElement).closest('button')) return;
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: pos.x,
        origY: pos.y,
      };
      e.preventDefault();
    },
    [pos.x, pos.y],
  );

  useEffect(() => {
    if (!open) return;
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPos(
        clampPos({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy }, width, height),
      );
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [open, width, height]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        left: isMobile ? 0 : pos.x,
        right: isMobile ? 0 : undefined,
        top: isMobile ? (minimized ? undefined : 0) : pos.y,
        bottom: isMobile && minimized ? 0 : undefined,
        width: isMobile ? '100vw' : width,
        height: isMobile ? (minimized ? 48 : '100dvh') : minimized ? 48 : height,
        background: '#fff',
        borderRadius: isMobile ? 0 : 10,
        boxShadow: '0 10px 28px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.06)',
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'height 0.18s ease',
      }}
    >
      {/* 标题栏 — 拖拽手柄 */}
      <div
        onMouseDown={handleMouseDown}
        style={{
          height: 40,
          padding: '0 8px 0 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#fafbfc',
          borderBottom: '1px solid #f0f1f3',
          cursor: 'move',
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        <Space size={6}>
          <PushpinOutlined style={{ color: '#1677ff', fontSize: 13 }} />
          <Typography.Text strong style={{ fontSize: 13 }}>
            {title}
          </Typography.Text>
        </Space>
        <Space size={2}>
          {extra}
          <Button
            type="text"
            size="small"
            icon={<MinusOutlined />}
            onClick={() => setMinimized((m) => !m)}
            title={minimized ? '展开' : '最小化'}
          />
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
            title="关闭"
          />
        </Space>
      </div>

      {/* 主体 — 最小化时不渲染内容, 避免占空间 */}
      {!minimized && (
        <div
          style={{
            padding: bodyPadding,
            overflowY: bodyScroll ? 'auto' : 'hidden',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            // 让内部 tab/列表/聊天计算高度时有上限
            maxHeight: isMobile ? 'calc(100dvh - 40px)' : height - 40,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
